import React, { useEffect, useRef, useState } from "react";
import "./NewSection.css";

const IMAGES = [
  "/images/chris-hero-pose.png",
  "/images/chris-on-the-phone.png",
  "/images/chris-greeting-customer.png",
  "/images/chris-on-a-lader.png",
];

const FEATURES = [
  {
    icon: "/icons/gaurantees_experienced-electricians.png",
    title: "Experienced Electricians",
    text:
      "All of our electricians are licensed and participate in ongoing technical and customer service training to ensure that you are always getting the best possible service.",
  },
  {
    icon: "/icons/gaurantees_transparent-pricing.png",
    title: "Transparent Pricing",
    text:
      "We will provide you with a detailed quote with no hidden costs. We want you to feel confident for choosing our team.",
  },
  {
    icon: "/icons/gaurantees_customer-satisfaction.png",
    title: "Customer Satisfaction",
    text:
      "We will guarantee your satisfaction by exceeding your expectations and will address all questions and concerns you may have.",
  },
  {
    icon: "/icons/gaurantees_localy-owned.png",
    title: "Locally Owned & Operated",
    text:
      "We are a locally owned and operated business that cares for the community and its people.",
  },
];

const NewSection = ({ onBookNowClick }) => {
  const sectionRef = useRef(null);
  const [progress, setProgress] = useState(0); // 0..1 scroll progress inside section
  const [pinState, setPinState] = useState("above"); // 'above' | 'fixed' | 'below'

  useEffect(() => {
    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const vv = window.visualViewport?.height || window.innerHeight;
      const navbar = 60; // fixed navbar height
      const pinnedHeight = Math.max(vv - navbar, 1);
      const rect = el.getBoundingClientRect();
      const sectionHeight = el.offsetHeight;
      const totalScrollable = Math.max(sectionHeight - pinnedHeight, 1);

      // Progress based on how far the section top has moved past the navbar line
      const delta = navbar - rect.top; // 0 when rect.top === navbar
      const scrolledWithin = Math.min(Math.max(delta, 0), totalScrollable);
      setProgress(scrolledWithin / totalScrollable);

      if (rect.top > navbar) {
        setPinState("above");
      } else if (rect.bottom > navbar + pinnedHeight) {
        setPinState("fixed");
      } else {
        setPinState("below");
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const imageCount = IMAGES.length;
  const featureCount = FEATURES.length;
  const totalSegments = featureCount + imageCount;
  const seg = 1 / totalSegments; // normalized segment length across features + images
  const t = progress; // 0..1 across full sequence
  const fImage = 0.2; // image fade fraction
  const fFeature = 0.5; // feature fade fraction within its segment

  // Our Process content (desktop grid and mobile one-by-one)
  const PROCESS = [
    {
      title: "Give Us a Call",
      text:
        "It is our mission to provide you with professional service, and we want to know how we can help you.",
    },
    {
      title: "Let’s Meet",
      text:
        "We can answer your questions and concerns. We will provide you with a detailed quote and timeline.",
    },
    {
      title: "Let’s Begin",
      text:
        "We will be on time, work safely and ensure our work exceeds your expectations.",
    },
  ];

  const featuresEnd = featureCount * seg;
  // Process opacity eases in with image 2 fade-in
  const processFadeInEnd = featuresEnd + fImage * seg;
  let processOpacity = 0;
  if (t >= featuresEnd && t < processFadeInEnd) {
    processOpacity = (t - featuresEnd) / (processFadeInEnd - featuresEnd);
  } else if (t >= processFadeInEnd) {
    processOpacity = 1;
  }

  // Active desktop card index (0..2) synced to images 2..4
  let activeProcessIdx = 0;
  if (t >= featuresEnd) {
    activeProcessIdx = Math.max(
      0,
      Math.min(PROCESS.length - 1, Math.floor((t - featuresEnd) / seg))
    );
  }

  return (
    <section ref={sectionRef} className="new-section">
      <div className={`new-section-viewport ${pinState === "fixed" ? "is-fixed" : ""} ${pinState === "below" ? "is-bottomed" : ""}`}>
        <div className="new-section-content">
          <div className="new-section-stage">
            {IMAGES.map((src, i) => {
              const featuresEnd = featureCount * seg;
              let opacity = 0;

              if (i === 0) {
                // Image 1: fade in at start, stay visible through all features, fade out as image 2 starts
                const fadeInEnd = fImage * seg;
                const fadeOutStart = featuresEnd;
                const fadeOutEnd = featuresEnd + fImage * seg;

                if (t >= 0 && t < fadeInEnd) {
                  const p = t / (fadeInEnd);
                  opacity = p;
                } else if (t >= fadeInEnd && t < fadeOutStart) {
                  opacity = 1;
                } else if (t >= fadeOutStart && t <= fadeOutEnd) {
                  const p = (t - fadeOutStart) / (fadeOutEnd - fadeOutStart);
                  opacity = 1 - p;
                } else {
                  opacity = 0;
                }
              } else {
                // Images 2..N: begin after all features, in order
                const imgStart = featuresEnd + (i - 1) * seg;
                const imgEnd = imgStart + seg;
                const fadeInStart = imgStart;
                const fadeInEnd = imgStart + fImage * seg;
                const fadeOutStart = imgEnd - fImage * seg;
                const fadeOutEnd = imgEnd;

                if (t >= fadeInStart && t < fadeInEnd) {
                  const p = (t - fadeInStart) / (fadeInEnd - fadeInStart);
                  opacity = p;
                } else if (t >= fadeInEnd && t < fadeOutStart) {
                  opacity = 1;
                } else if (t >= fadeOutStart && t <= fadeOutEnd) {
                  const p = (t - fadeOutStart) / (fadeOutEnd - fadeOutStart);
                  opacity = 1 - p;
                } else {
                  opacity = 0;
                }
              }

              return (
                <div key={i} className="new-section-image" style={{ opacity }}>
                  <img src={src} alt={`Showcase ${i + 1}`} />
                </div>
              );
            })}
          </div>
          {/* Desktop features grid (hidden on mobile) */}
          <div className="new-section-features new-section-features--desktop">
            {(() => {
              // Global fade-out for all features when image 1 begins to fade out
              const featuresEnd = featureCount * seg;
              const fadeOutStart = featuresEnd;
              const fadeOutEnd = featuresEnd + fImage * seg;
              let featuresGlobal = 1;
              if (t >= fadeOutStart && t <= fadeOutEnd) {
                featuresGlobal = 1 - (t - fadeOutStart) / (fadeOutEnd - fadeOutStart);
              } else if (t > fadeOutEnd) {
                featuresGlobal = 0;
              }
              return FEATURES.map((ftr, i) => {
                const featStart = i * seg;
                const featEnd = (i + 1) * seg;
                const fadeInStart = featStart;
                const fadeInEnd = featStart + fFeature * seg;
                let opacity = 0;
                if (t >= fadeInStart && t < fadeInEnd) {
                  const p = (t - fadeInStart) / (fadeInEnd - fadeInStart);
                  opacity = p;
                } else if (t >= fadeInEnd && t < fadeOutStart) {
                  opacity = 1;
                }
                opacity *= featuresGlobal;
                return (
                  <div key={i} className="feature-item" style={{ opacity }}>
                    <img src={ftr.icon} alt={ftr.title} className="feature-icon" />
                    <div className="feature-text">
                      <h3>{ftr.title}</h3>
                      <p>{ftr.text}</p>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
          {/* Desktop: Our Process cards appear with image 2 and scale with image 2/3/4 */}
          <div className="process-desktop" style={{ opacity: processOpacity }}>
            <h2 className="process-heading">Our Process</h2>
            <div className="process-grid">
              {PROCESS.map((card, i) => {
                const scale = i === activeProcessIdx ? 1.08 : 1.0;
                return (
                  <div
                    key={i}
                    className={`process-card ${i === activeProcessIdx ? 'active' : ''}`}
                    style={{ transform: `scale(${scale})` }}
                  >
                    <h3>{card.title}</h3>
                    <p>{card.text}</p>
                  </div>
                );
              })}
            </div>
            <div className="process-cta">
              <button className="schedule-appointment-button" onClick={onBookNowClick}>Schedule Appointment</button>
            </div>
          </div>

          {/* Mobile features list (positioned at viewport bottom) */}
          <div className="new-section-features new-section-features--mobile">
            {(() => {
              const featuresEnd = featureCount * seg;
              const globalFadeStart = featuresEnd;
              const globalFadeEnd = featuresEnd + fImage * seg;
              return FEATURES.map((ftr, i) => {
                // Mobile: show exactly one feature at a time at the bottom
                const featStart = i * seg;
                const featEnd = (i + 1) * seg;
                const fadeInStart = featStart;
                const fadeInEnd = featStart + fFeature * seg;
                const fadeOutStart = featEnd - fFeature * seg;
                const fadeOutEnd = featEnd;
                let opacity = 0;
                if (t >= fadeInStart && t < fadeInEnd) {
                  const p = (t - fadeInStart) / (fadeInEnd - fadeInStart);
                  opacity = p;
                } else if (t >= fadeInEnd && t < fadeOutStart) {
                  opacity = 1;
                } else if (t >= fadeOutStart && t <= fadeOutEnd) {
                  const p = (t - fadeOutStart) / (fadeOutEnd - fadeOutStart);
                  opacity = 1 - p;
                } else {
                  opacity = 0;
                }
                // Apply global fade when image 1 begins to fade
                if (t >= globalFadeStart && t <= globalFadeEnd) {
                  const g = 1 - (t - globalFadeStart) / (globalFadeEnd - globalFadeStart);
                  opacity *= g;
                } else if (t > globalFadeEnd) {
                  opacity = 0;
                }
                return (
                  <div key={i} className="feature-item" style={{ opacity }}>
                    <img src={ftr.icon} alt={ftr.title} className="feature-icon" />
                    <div className="feature-text">
                      <h3>{ftr.title}</h3>
                      <p>{ftr.text}</p>
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          {/* Mobile: Our Process cards, one at a time, synced with images 2..4 */}
          <div className="process-mobile">
            {PROCESS.map((card, i) => {
              // Card i maps to image (i+2): start at featuresEnd + i*seg
              const cardStart = featuresEnd + i * seg;
              const cardEnd = cardStart + seg;
              const fadeInStart = cardStart;
              const fadeInEnd = cardStart + fImage * seg;
              const fadeOutStart = cardEnd - fImage * seg;
              const fadeOutEnd = cardEnd;
              let opacity = 0;
              if (t >= fadeInStart && t < fadeInEnd) {
                opacity = (t - fadeInStart) / (fadeInEnd - fadeInStart);
              } else if (t >= fadeInEnd && t < fadeOutStart) {
                opacity = 1;
              } else if (t >= fadeOutStart && t <= fadeOutEnd) {
                opacity = 1 - (t - fadeOutStart) / (fadeOutEnd - fadeOutStart);
              }
              return (
                <div key={i} className="process-card-mobile" style={{ opacity }}>
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </div>
              );
            })}
            <div className="process-cta-mobile">
              <button className="schedule-appointment-button" onClick={onBookNowClick}>Schedule Appointment</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default React.memo(NewSection);
